import { useEffect, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import localLawService from "api/localLawService";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface Props {
  actId: string;
  onClose: () => void;
  onSaved: () => void;
}

// Ustawienia prezentacji aktu w Biuletynie Informacji Publicznej.
// Odwzorowanie sekcji BIP z metryki aktu w Magistracie (Kwantum.BIP + MetricDescriptionTab):
// osobny tytuł i opis dla BIP, kategoria (dział biuletynu) oraz data publikacji.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const toDateInput = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";

const BipModal = ({ actId, onClose, onSaved }: Props) => {
  const actQuery = localLawService.useGetAct(actId);
  const { mutate: saveBip, isLoading: saving } = localLawService.useSaveBip();

  const [publish, setPublish] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState("");

  const act = actQuery.data?.data;

  useEffect(() => {
    if (!act) return;

    setPublish(act.PublishInBip);
    setTitle(act.BipTitle ?? "");
    setDescription(act.BipDescription ?? "");
    setCategory(act.BipCategory ?? "");
    setDate(toDateInput(act.BipPublicationDate));
  }, [act]);

  const submit = () => {
    saveBip(
      {
        id: actId,
        request: {
          PublishInBip: publish,
          BipTitle: title.trim() || null,
          BipDescription: description.trim() || null,
          BipCategory: category.trim() || null,
          BipPublicationDate: date || null,
        },
      },
      {
        onSuccess: onSaved,
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać ustawień BIP."),
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Prezentacja w BIP</div>

        <div className={styles.modalBody}>
          {actQuery.isLoading ? (
            <Loading />
          ) : (
            <>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={publish}
                  onChange={(e) => setPublish(e.target.checked)}
                />
                Publikuj akt w Biuletynie Informacji Publicznej
              </label>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  Tytuł pozycji w BIP (puste = tytuł z metryki)
                </label>
                <input
                  className={styles.textInput}
                  value={title}
                  disabled={!publish}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Opis</label>
                <textarea
                  className={styles.textArea}
                  value={description}
                  disabled={!publish}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Kategoria / dział BIP</label>
                  <input
                    className={styles.textInput}
                    value={category}
                    disabled={!publish}
                    placeholder="np. Uchwały Rady Miejskiej"
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Data publikacji</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={date}
                    disabled={!publish}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>

              {publish && !act?.HasXml && (
                <span className={styles.hint}>
                  Akt nie ma jeszcze wygenerowanego XML — w BIP pojawi się wyłącznie plik
                  dokumentu, bez treści przeszukiwalnej.
                </span>
              )}
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button text="Zapisz" loading={saving} onClick={submit} />
        </div>
      </div>
    </div>
  );
};

export default BipModal;
